import { useEffect, useMemo, useState } from "react";
import { useAuth } from "context/AuthContext";
import { getToken } from "services/auth.service";
import {
  getCachedAuthorization,
  hasAnyPermission,
  hasPermission,
  hasRole as hasCachedRole,
  loadAuthorization,
} from "auth/authorization.service";
import { PERMISSIONS } from "auth/permissionCodes";

// Hook tương thích với logic cũ, nhưng nguồn dữ liệu mới là /api/me/authorization.
export const usePermissions = () => {
  const { user, isAuthenticated } = useAuth();
  const [authorization, setAuthorization] = useState(() => getCachedAuthorization());
  const [loading, setLoading] = useState(Boolean(getToken()) && !authorization);

  useEffect(() => {
    let isMounted = true;

    const load = async () => {
      if (!isAuthenticated || !user || !getToken()) {
        if (isMounted) {
          setAuthorization(null);
          setLoading(false);
        }
        return;
      }

      const cached = getCachedAuthorization();
      if (cached) {
        if (isMounted) {
          setAuthorization(cached);
          setLoading(false);
        }
        return;
      }

      try {
        setLoading(true);
        const data = await loadAuthorization();
        if (isMounted) {
          setAuthorization(data);
        }
      } catch {
        if (isMounted) {
          setAuthorization(null);
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    load();

    const handleAuthorizationChanged = (event) => {
      if (isMounted) {
        setAuthorization(event.detail || getCachedAuthorization());
      }
    };

    window.addEventListener("authorization:changed", handleAuthorizationChanged);
    return () => {
      isMounted = false;
      window.removeEventListener("authorization:changed", handleAuthorizationChanged);
    };
  }, [user, isAuthenticated]);

  const userRoles = useMemo(() => authorization?.roles || [], [authorization]);

  const hasRole = (roleName) => {
    if (loading || !authorization) return false;
    return hasCachedRole(roleName, authorization);
  };

  const hasAnyRole = (roleNames) => {
    if (loading || !authorization) return false;
    return roleNames.some((roleName) => hasRole(roleName));
  };

  const isAdmin = () => Boolean(authorization?.isAdmin) || hasRole("Admin");

  const can = (permissionCode) => hasPermission(permissionCode, authorization);
  const canAny = (permissionCodes) => hasAnyPermission(permissionCodes, authorization);

  const canAccessUserManagement = () => can(PERMISSIONS.SYSTEM.USER_READ);
  const canAccessPermissionManagement = () =>
    canAny([
      PERMISSIONS.SYSTEM.PERMISSION_FUNCTION_READ,
      PERMISSIONS.SYSTEM.PERMISSION_APPLICATION_READ,
    ]);
  const canAccessSystemSettings = () => can(PERMISSIONS.APPLICATION.SYSTEM_ACCESS);
  const canAccessContracts = () => can(PERMISSIONS.CONTRACT.CONTRACT_READ);
  const canCreateContracts = () => can(PERMISSIONS.CONTRACT.CONTRACT_CREATE);
  const canEditContracts = () => can(PERMISSIONS.CONTRACT.CONTRACT_UPDATE);
  const canDeleteContracts = () => can(PERMISSIONS.CONTRACT.CONTRACT_DELETE);
  const canViewContracts = () => can(PERMISSIONS.CONTRACT.CONTRACT_READ);
  const canAccessPersonnelManagement = () => can(PERMISSIONS.SYSTEM.PERSONNEL_READ);

  return {
    user,
    authorization,
    userRoles,
    permissions: authorization?.permissions || [],
    applications: authorization?.applications || [],
    loading,
    hasRole,
    hasAnyRole,
    isAdmin,
    can,
    canAny,
    canAccessUserManagement,
    canAccessPermissionManagement,
    canAccessSystemSettings,
    canAccessContracts,
    canCreateContracts,
    canEditContracts,
    canDeleteContracts,
    canViewContracts,
    canAccessPersonnelManagement,
  };
};

export const PermissionGuard = ({
  checkFunction = null,
  fallback = null,
  children,
}) => {
  const permissions = usePermissions();
  const hasAccess = checkFunction && typeof checkFunction === "function"
    ? checkFunction(permissions)
    : true;

  if (!hasAccess) {
    return fallback;
  }

  return children;
};
